import { v4 as uuidv4 } from 'uuid';

/**
 * Services seed data with complete information for all services.
 * Centralized location for service data to avoid duplication.
 */
export const servicesSeedData = [
  {
    id: uuidv4(),
    title: 'AI Advisory',
    shortDescription:
      'Independent counsel for leadership teams on AI investment discipline, risk, and organizational alignment.',
    description:
      'Enterprise AI rarely fails for lack of models—it fails when leadership cannot reconcile fragmented initiatives, unclear ownership, and spend that outpaces return anyone can explain. We work alongside CEOs, CFOs, and their leadership teams to sharpen judgment: where AI genuinely matters, what to pause, how to govern it, and how to sequence investment against operating reality. We advise; we do not implement or resell platforms.',
    slug: 'ai-advisory',
    icon: 'ai',
    keyFeatures: [
      'How AI initiatives compete for capital and operating attention across the enterprise',
      'Ownership, escalation, and governance when models influence customer and employee decisions',
      'Whether data, platforms, and vendor relationships support what leadership is being asked to fund',
      'Build vs. buy trade-offs and the full economics beyond license fees',
      'Regulatory, privacy, and reputational exposure as AI moves into core workflows',
      'How to pressure-test vendor claims and internal business cases before commitments harden',
    ],
    benefits: [
      'A clearer picture of where AI spend is going and who is accountable for results',
      'Shared criteria for when to continue, redirect, or stop—not left to individual functions',
      'Governance thinking that legal, audit, and operations can engage with constructively',
      'Investment sequencing that respects fiscal constraints and delivery capacity',
      'Less dependence on vendor narratives when architecture and contract choices are irreversible',
    ],
    useCases: [
      'Years of decentralized experimentation with no enterprise view of cost or risk',
      'Leadership scrutiny following a visible AI failure or near-miss',
      'Portfolio overlap after merger, acquisition, or major reorganization',
      'Incoming regulation or sector guidance on automated decision-making',
      'Foundation-model, cloud AI, or agentic architecture decisions with long lock-in',
      'C-suite misalignment ahead of a multiyear capital commitment',
    ],
    outcomes: [
      'Sharper view of which AI bets warrant attention and which do not',
      'Shared language for governance conversations across legal, risk, and operations',
      'Investment choices leadership can explain on their own terms',
      'Ongoing discipline for reviewing AI spend against original intent',
    ],
    timeline: {
      discovery:
        'We start by building shared understanding: where AI already shapes revenue, cost, and risk; what is production versus experiment; where dependencies on data and third parties are thin; and where exposure is growing faster than oversight.',
      strategy:
        'We work through the choices that follow: which initiatives deserve focus, how ownership should sit in the operating model, what guardrails matter as models touch customers and employees, and how investment fits the capital and delivery rhythm you already have.',
      implementation:
        'Where leadership chooses to proceed, we advise on how to test commitments under explicit assumptions—what would prove value, what would trigger a stop, and what must hold before anything expands beyond a controlled footprint.',
    },
  },
  {
    id: uuidv4(),
    title: 'Data Advisory',
    shortDescription:
      'Independent perspective on data value, consolidation, and governance—before AI or analytics scale.',
    description:
      'Data estates often grow faster than leadership can explain—parallel platforms, inconsistent definitions, and spending that outpaced governance. We work with CEOs, CFOs, and boards as outside counsel: pressure-testing assumptions, surfacing trade-offs, and helping leadership form judgments that hold up under scrutiny—without defaulting to another migration program.',
    slug: 'data-advisory',
    icon: 'data',
    keyFeatures: [
      'How data spend connects to revenue, cost, and operating risk',
      'Whether architecture and vendor choices still fit the business and AI agenda',
      'Where ownership for quality, lineage, privacy, and access is unclear or disputed',
      'Which platform investments compete, overlap, or lack defensible return',
      'Regulatory and reputational exposure in how data is collected, shared, and used',
      'Gaps between what AI initiatives assume and what the estate can actually support',
    ],
    benefits: [
      'Shared judgment among executives on what data matters—and what does not',
      'Hard trade-offs surfaced before capital commits to platforms or vendors',
      'Less exposure from data that lacks clear ownership or control',
      'Sharper instincts on which initiatives warrant attention and which should wait',
      'Board and regulator conversations grounded in substance, not vendor narratives',
      'A clearer link between data foundations and the pace of AI or analytics',
    ],
    useCases: [
      'Board or audit committee questions following a breach, finding, or consent order',
      'Cloud or lakehouse direction stalled by conflicting internal proposals',
      'AI initiatives underperforming because source data cannot be trusted',
      'Post-merger tension over overlapping warehouses, lakes, or vendor contracts',
      'Regulatory inquiry into customer, employee, or financial data handling',
      'CFO or CIO concern over rising data platform spend without clear return',
      'Leadership alignment before a major data budget cycle or enterprise AI push',
    ],
    outcomes: [],
    timeline: {
      discovery:
        'Understand how the data estate connects to revenue, cost, and risk—and where ownership, vendor sprawl, or quality gaps undermine confidence in current decisions.',
      design:
        'Work through architecture and governance choices together: what deserves investment, what should be consolidated, and how leadership will recognize real progress.',
      implementation:
        'Remain available as independent judgment on execution—vendor claims, internal trade-offs, and whether conditions are right before analytics or AI move forward.',
    },
  },
  {
    id: uuidv4(),
    title: 'Agentic AI Advisory',
    shortDescription:
      'Independent counsel when systems cross from recommendation to action—and leadership must decide what authority to delegate.',
    description:
      'Agentic AI executes: it triggers workflows, commits resources, and acts across systems without waiting for a human click. That is a governance question before it is a technology question. Most organizations are authorizing experiments faster than they can explain accountability, reversal, or board oversight. We work alongside CEOs, COOs, general counsel, and risk committees to sharpen judgment on where bounded automation is worth the exposure, where human approval must remain, and how to think about evidence before authority expands. We advise; we do not build agents or resell platforms.',
    slug: 'agentic-ai-advisory',
    icon: 'agent',
    keyFeatures: [
      'Where agentic automation touches revenue, cost, liability, and operational control',
      'What authority the organization is prepared to delegate—and where override must remain',
      'Whether existing audit, compliance, and access controls can govern systems that act autonomously',
      'How vendor claims about autonomous workflows map to actual risk and total cost',
      'When a pilot has produced enough evidence to expand, pause, or stop—not merely continue',
      'How boards and regulators will understand systems that commit on the company\'s behalf',
    ],
    benefits: [
      'Clearer thinking on whether a workflow should be automated at all—not assumed by default',
      'Shared criteria for authority boundaries before engineering or vendor momentum takes over',
      'Constructive engagement between operations, legal, and risk on systems that act without sign-off',
      'Less exposure from agents running outside scope leadership would actually defend',
      'Vendor and architecture choices examined before contract and lock-in make reversal costly',
      'Board and regulator conversations grounded in how authority is held—not in demo narratives',
    ],
    useCases: [
      'Board or audit committee concern after an agent operated outside intended boundaries',
      'COO pressure to automate end-to-end workflows against unresolved operational and reputational risk',
      'General counsel review of liability when systems execute without human approval',
      'CFO scrutiny of agent economics, reliability, and vendor dependency',
      'Post-incident reset of enterprise permissions and oversight after a near-miss or failure',
      'C-suite misalignment before an agent platform, vendor contract, or major capital allocation',
    ],
    outcomes: [],
    timeline: {
      discovery:
        'We begin with the workflow and the decision being delegated: what systems and data are involved, what manual controls exist today, and what regulatory or contractual limits apply. The aim is a shared view of what the organization is—and is not—willing to authorize without human approval.',
      design:
        'We work through the choices that follow: where authority should stop, who retains override, how oversight aligns with audit and compliance, and what would count as evidence before anything expands.',
      production:
        'Where leadership chooses to proceed, we advise on how to test authority under explicit assumptions—what would justify expansion, what would trigger a pause, and what must hold before scope widens.',
    },
  },
];
